/* eslint-disable no-console */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { atom, useAtom, useAtomValue } from 'jotai';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';

import { Button as ButtonV2, Heading, Modal, ModalContent } from '@restworld/ui-ds-base';
import { Worker } from '@restworld/utility-types';
import { useWorkersService } from '@restworld/data-services';
import { useAuth } from '@restworld/feature-authentication';
import { useKv, useVariableOptions } from '@restworld/utils-common';

import { workerAtom } from '../components/global-state.worker-data';

const isGlobalJobPositionPreferencesModalVisible = atom<boolean>(false);

const useHasWorkerInterestedInAboardJobsConfirmPopupShownMutation = () => {
  const { setKV } = useKv();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (workerInterestedAboardJobsConfirmation: boolean) =>
      setKV({
        key: user?.id,
        value: JSON.stringify({
          workerInterestedAboardJobsConfirmation,
        }),
        type: 'worker_interested_aboard_jobs_confirmation',
      }),
    onError: (error) => {
      console.error('Error updating saved research', error);
    },
  });
};

const useWorkersWorkingAboardPreferenceMutation = () => {
  const workerService = useWorkersService();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ params }: { params: Partial<Worker> }) =>
      await workerService.updateWorker({
        workerId: user?.id,
        params,
      }),
    onError: (error) => {
      console.error('Error updating worker', error);
    },
  });
};
const useGlobalJobPositionPreferencesModal = () => {
  const { user } = useAuth();
  const router = useRouter();
  const [isWorkerInterestedInGlobalOffers, setIsWorkerInterested] = useState(false);
  const [isModalVisible, toggleModalVisibility] = useAtom(isGlobalJobPositionPreferencesModalVisible);
  const { mutate: showPopupMutation } = useHasWorkerInterestedInAboardJobsConfirmPopupShownMutation();
  const { mutate: updateWorkerPreferences, isSuccess, isPending } = useWorkersWorkingAboardPreferenceMutation();
  const { fetchVariable, variableOptions } = useVariableOptions();
  const workerData = useAtomValue(workerAtom).data;
  const { getKV } = useKv();

  const displacementPreferencesAboardOption = useMemo(
    () => variableOptions?.displacement_preference?.find((d) => d.key === 'displacement_abroad'),
    [variableOptions?.displacement_preference]
  );

  const isSettingsPage = router.pathname === '/impostazioni';
  const isProfilePage = router.pathname === '/profilo';
  const isJobPositionPage = router.pathname.includes('/posizione/');

  const getConfirmationFlag = useCallback(async () => {
    const flagData = (await getKV(user?.id))?.value;
    if (flagData) {
      const parsedFlagData = JSON.parse(flagData)?.workerInterestedAboardJobsConfirmation
        ? JSON.parse(JSON.parse(flagData)?.workerInterestedAboardJobsConfirmation)
        : '';
      if (
        !parsedFlagData &&
        (isProfilePage || isSettingsPage || isJobPositionPage) &&
        !workerData?.displacement_preferences?.length
      ) {
        setTimeout(() => {
          toggleModalVisibility(true);
        }, 12000);
      }
    }
  }, [
    getKV,
    user?.id,
    isProfilePage,
    isSettingsPage,
    isJobPositionPage,
    workerData?.displacement_preferences?.length,
    toggleModalVisibility,
  ]);

  useEffect(() => {
    if (user?.id) getConfirmationFlag();
  }, [getConfirmationFlag, user?.id]);

  const onInterestedSection = useCallback(() => {
    const updatedPreferences = workerData?.displacement_preferences
      ? [
          ...workerData.displacement_preferences
            .filter((v) => v.displacement_preference_var.key !== 'displacement_abroad')
            .map((v) => ({
              displacement_preference_var: v.displacement_preference_var,
            })),
          {
            displacement_preference_var: displacementPreferencesAboardOption,
          },
        ]
      : [
          {
            displacement_preference_var: displacementPreferencesAboardOption,
          },
        ];

    updateWorkerPreferences({ params: { displacement_preferences: updatedPreferences } });
    showPopupMutation(true);
  }, [
    displacementPreferencesAboardOption,
    showPopupMutation,
    updateWorkerPreferences,
    workerData?.displacement_preferences,
  ]);

  const onClose = useCallback(() => {
    toggleModalVisibility(false);
    setTimeout(() => {
      setIsWorkerInterested(false);
    }, 250);
  }, [toggleModalVisibility]);

  const onHandleWorkerNotInterested = useCallback(() => {
    showPopupMutation(true);
    toggleModalVisibility(false);
  }, [toggleModalVisibility, showPopupMutation]);

  useEffect(() => {
    fetchVariable(['displacement_preference']);
  }, [fetchVariable]);

  useEffect(() => {
    if (isSuccess) setIsWorkerInterested(true);
  }, [isSuccess]);

  return {
    isModalVisible,
    isWorkerInterestedInGlobalOffers,
    isPending,
    onInterestedSection,
    onClose,
    onHandleWorkerNotInterested,
  };
};

export const GlobalJobPositionPreferencesModal = () => {
  const {
    isModalVisible,
    isWorkerInterestedInGlobalOffers,
    isPending,
    onInterestedSection,
    onClose,
    onHandleWorkerNotInterested,
  } = useGlobalJobPositionPreferencesModal();
  const { t } = useTranslation('common');
  const modalTitle = (
    <Heading size={3} weight="bold" color="neutral" align="center">
      {isWorkerInterestedInGlobalOffers ? t('profile_updated') : t('interested_in_foreign_offers')}
    </Heading>
  );

  const modalFooter = isWorkerInterestedInGlobalOffers ? (
    <div className="w-full flex justify-center">
      <ButtonV2 variant="primary" onClick={onClose}>
        {t('close')}
      </ButtonV2>
    </div>
  ) : (
    <div className="w-full flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-center">
      <ButtonV2 variant="tertiary" onClick={onHandleWorkerNotInterested}>
        {t('i_am_not_interested')}
      </ButtonV2>
      <ButtonV2 loading={isPending} variant="primary" onClick={onInterestedSection}>
        {t('yes_interested')}
      </ButtonV2>
    </div>
  );

  return (
    <Modal
      open={isModalVisible}
      onOpenChange={() => {
        /* */
      }}
    >
      <ModalContent title={modalTitle} footer={modalFooter} className="w-full lg:w-[42%] pb-4" showCloseIcon={false}>
        <Heading size={5} color="neutral-light">
          {isWorkerInterestedInGlobalOffers ? (
            <Heading size={5} color="neutral-light">
              {t('thanks_for_response')}{' '}
              <Link href="/profilo" className="underline underline-offset-4">
                {t('restworld_profile')}
              </Link>
            </Heading>
          ) : (
            t('global_opportunities')
          )}
        </Heading>
      </ModalContent>
    </Modal>
  );
};
